import React, { useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Button,
  Skeleton,
  Collapse,
  IconButton,
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  ReadMore,
  Create,
  Person,
  Room,
} from '@mui/icons-material'
import { ItemResponse } from 'common/types'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'
import { getTrayStatusDisplay } from 'utils/getTrayStatusDisplay'
import dayjs from 'lib/dayjs'
import { useNavigate } from 'react-router-dom'
import { useUser } from 'app/User'
import { navigateToTrayDetails } from 'lib/utils/navigationUtils'
import { useUpdateTrayItem } from 'lib/apollo/hooks'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import {
  TrayStatus,
  TrayStatusDetails,
} from 'components/organisms/EditTrayModal/EditTrayModal.types'
import { SPDContextProps } from 'views/SPDLayout/SPD/SPD.types'
import EditTrayStatusModal from 'components/organisms/EditTrayStatusModal/EditTrayStatusModal'
import TrayRejectionDetailsModal from 'components/organisms/TrayRejectionDetailsModal/TrayRejectionDetailsModal'
import { ChooseTrayLocationModal } from 'components/organisms/ChooseTrayLocationModal/ChooseTrayLocationModal'
import EditTrayModal from 'components/organisms/EditTrayModal/EditTrayModal'
import './SurgeryTraysDisplay.scss'
import classNames from 'classnames'

interface SurgeryTraysDisplayProps {
  surgeryId: string
  trays: ItemResponse[]
  loading: boolean
  error?: Error | null
  expanded?: boolean
  onToggleExpanded?: () => void
  onViewTrayDetails?: (tray: ItemResponse) => void
  onAssignTray?: (tray: ItemResponse) => void
  onEditTrayStatus?: (tray: ItemResponse) => void
  onSetTrayLocation?: (tray: ItemResponse) => void
  onLoadTray?: (tray: ItemResponse) => void
  onTraysUpdate?: (updatedTrays: ItemResponse[]) => void
  onTrayAssignmentChange?: () => void
  beforeDate?: string
  afterDate?: string
}

/**
 * Component to display trays assigned to a surgery
 * Follows clean code principles with clear separation of concerns
 */
const SurgeryTraysDisplay: React.FC<SurgeryTraysDisplayProps> = ({
  surgeryId,
  trays,
  loading,
  error,
  expanded = false,
  onToggleExpanded,
  onViewTrayDetails,
  onAssignTray,
  onEditTrayStatus,
  onSetTrayLocation,
  onLoadTray,
  onTraysUpdate,
  onTrayAssignmentChange,
  beforeDate,
  afterDate,
}) => {
  const navigate = useNavigate()
  const { user } = useUser()
  const { editTrayItem } = useUpdateTrayItem()
  const { setTrayData } = useSPD()

  // State management for modals
  const [selectedTray, setSelectedTray] = useState<
    SPDContextProps['trayData'] | null
  >(null)
  const [isListOfSurgeriesOpen, setIsListOfSurgeriesOpen] =
    useState<boolean>(false)
  const [isEditTrayStatusModalOpen, setIsEditTrayStatusModalOpen] =
    useState<boolean>(false)
  const [isRejectionDetailsModalOpen, setIsRejectionDetailsModalOpen] =
    useState<boolean>(false)
  const [rejectionDetails, setRejectionDetails] =
    useState<TrayStatusDetails | null>(null)
  const [
    isShowChooseTrayLocationModalVisible,
    setIsShowChooseTrayLocationModalVisible,
  ] = useState<boolean>(false)
  const [isUpdatingTrayStatus, setIsUpdatingTrayStatus] =
    useState<boolean>(false)

  const isSPD = user?.roles?.includes('MGAT_SPD')
  const isRep = user?.roles?.includes('MGAT_REP')
  const isNurse =
    user?.roles?.includes('MGAT_NURSE') && !user?.roles?.includes('MGAT_SPD')

  const selectedTrayCategory = selectedTray
    ? extractTrayJSONData(selectedTray.trayItem)?.trayCategory
    : undefined

  // Early return for loading state
  if (loading) {
    return (
      <Box className="surgery-trays-display">
        <Box className={'trays-header'}>
          <Typography variant="h6">Assigned Trays</Typography>
          <Skeleton variant="rectangular" width={100} height={32} />
        </Box>
        <Box className="trays-content">
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={120}
              sx={{ mb: 1 }}
            />
          ))}
        </Box>
      </Box>
    )
  }

  // Early return for error state
  if (error) {
    return (
      <Box className="surgery-trays-display">
        <Typography color="error" variant="body2">
          Error loading trays: {error.message}
        </Typography>
      </Box>
    )
  }

  // Early return for no trays
  if (!trays || trays.length === 0) {
    return null
  }

  const handleViewTrayDetails = (tray: ItemResponse) => {
    if (onViewTrayDetails) {
      onViewTrayDetails(tray)
    } else {
      // Default navigation behavior with source tracking
      if (tray.barcode) {
        navigateToTrayDetails(
          navigate,
          tray.barcode, // If this should be a tray barcode, adjust accordingly
          {
            fromSurgeries: true,
            selectedSurgeryId: surgeryId,
            beforeDate: beforeDate ? String(beforeDate) : undefined,
            afterDate: afterDate ? String(afterDate) : undefined,
          }
        )
      }
    }
  }

  const handleAssignTray = (tray: ItemResponse) => {
    if (onAssignTray) {
      onAssignTray(tray)
    } else {
      // Default assign to surgery behavior
      const spdTray: SPDContextProps['trayData'] = {
        trayID: `${tray.id}`,
        trayItem: tray,
      }
      setIsListOfSurgeriesOpen(true)
      setSelectedTray(spdTray)
    }
  }

  const handleEditTrayStatus = (tray: ItemResponse) => {
    if (onEditTrayStatus) {
      onEditTrayStatus(tray)
    } else {
      // Default edit status behavior
      setSelectedTray({
        trayID: `${tray.id}`,
        trayItem: tray,
      })
      setIsEditTrayStatusModalOpen(true)
    }
  }

  const handleSetTrayLocation = (tray: ItemResponse) => {
    if (onSetTrayLocation) {
      onSetTrayLocation(tray)
    } else {
      // Default set location behavior
      setIsShowChooseTrayLocationModalVisible(true)
      setTrayData({
        trayID: `${tray.id}`,
        trayItem: tray,
      })
    }
  }

  const handleListOfSurgeriesClose = () => {
    setIsListOfSurgeriesOpen(false)
    setSelectedTray(null)
  }

  const handleEditTrayStatusChange = async (status: TrayStatus) => {
    setIsUpdatingTrayStatus(true)
    const trayItem = selectedTray?.trayItem

    if (!selectedTray || !trayItem) {
      return
    }

    const trayDetails = extractTrayJSONData(selectedTray.trayItem)

    try {
      const statusDetails: TrayStatusDetails = {
        ...(trayDetails?.statusDetails || {}),
        isRejected: status === 'rejected',
        isApproved: status === 'approved',
        lastUpdatedBy: user?.email,
        lastUpdatedAt: new Date().toISOString(),
      }

      const newProductDetails = JSON.stringify({
        ...trayDetails,
        status,
        ...(status === 'unAssigned'
          ? { surgeryId: undefined, surgeryDetails: undefined }
          : {}),
        statusDetails,
      })

      const { subItems, ...newTrayItem } = trayItem

      const editResult = await editTrayItem({
        ...newTrayItem,
        productDetails: newProductDetails,
      })

      if (!editResult) {
        throw new Error('Failed to edit tray item.')
      }

      // Update local state immediately
      const updatedTray = { ...trayItem, productDetails: newProductDetails }

      if (status === 'unAssigned') {
        // Remove tray from surgery's tray list if it becomes unassigned
        const updatedTrays = trays.filter((tray) => tray.id !== trayItem.id)
        onTraysUpdate?.(updatedTrays)
      } else {
        // Update the tray in the list
        const updatedTrays = trays.map((tray) =>
          tray.id === trayItem.id ? updatedTray : tray
        )
        onTraysUpdate?.(updatedTrays)
      }

      setIsEditTrayStatusModalOpen(false)
      setSelectedTray(null)
    } catch (error) {
      console.error(error)
    } finally {
      setIsUpdatingTrayStatus(false)
    }
  }

  const handleViewRejectionDetails = (rejectionDetails: TrayStatusDetails) => {
    setRejectionDetails(rejectionDetails)
    setIsRejectionDetailsModalOpen(true)
  }

  const handleRejectionDetailsModalClose = () => {
    setIsRejectionDetailsModalOpen(false)
    setRejectionDetails(null)
  }

  const handleChooseTrayLocationModalClose = () => {
    setIsShowChooseTrayLocationModalVisible(false)
    setTrayData(null)
  }

  const handleFixRejectedTray = (tray: ItemResponse) => {
    const trayData = {
      trayID: tray.barcode || '',
      trayItem: tray,
      isRejectedTrayFix: true,
    }
    const encodedTrayData = Buffer.from(JSON.stringify(trayData)).toString(
      'base64'
    )
    navigate(`./add-tray?rejectedTrayData=${encodedTrayData}`)
  }

  // Handle successful surgery assignment
  const handleSurgeryAssignmentSuccess = (updatedTray: ItemResponse) => {
    // If the tray is assigned to a different surgery, remove it from this surgery's list
    const trayDetails = extractTrayJSONData(updatedTray)
    if (trayDetails?.surgeryId && trayDetails.surgeryId !== surgeryId) {
      const updatedTrays = trays.filter((tray) => tray.id !== updatedTray.id)
      onTraysUpdate?.(updatedTrays)

      // Trigger comprehensive refetch to update all surgery tray lists
      // This will update both the source surgery (removing tray) and target surgery (adding tray)
      onTrayAssignmentChange?.()
    } else {
      // Update the tray in the list if it's still assigned to this surgery
      const updatedTrays = trays.map((tray) =>
        tray.id === updatedTray.id ? updatedTray : tray
      )
      onTraysUpdate?.(updatedTrays)
    }

    setIsListOfSurgeriesOpen(false)
    setSelectedTray(null)
  }

  // Handle successful location update
  const handleLocationUpdateSuccess = (updatedTray: ItemResponse) => {
    const updatedTrays = trays.map((tray) =>
      tray.id === updatedTray.id ? updatedTray : tray
    )
    onTraysUpdate?.(updatedTrays)

    setIsShowChooseTrayLocationModalVisible(false)
    setTrayData(null)
  }

  const renderTrayCard = (tray: ItemResponse) => {
    const trayDetails = extractTrayJSONData(tray)
    const trayStatusDisplay = getTrayStatusDisplay(trayDetails?.status)

    // Get tray image based on category
    const facilityTrayImg = require('assets/icons/images/facility-tray.jpg')
    const consignedTrayImg = require('assets/icons/images/consigned-tray.jpg')
    const loanerTrayImg = require('assets/icons/images/loaner-tray.jpg')
    const trayImg =
      trayDetails?.trayCategory === 'facility'
        ? facilityTrayImg
        : trayDetails?.trayCategory === 'consigned'
        ? consignedTrayImg
        : loanerTrayImg

    // Add check for rep notification status
    const isRepNotified = ['readyForPickup', 'rejected'].includes(
      trayDetails?.status || ''
    )
    const isRejected = trayDetails?.status === 'rejected'

    return (
      <Card
        key={tray.id}
        className="tray-card"
        id={`tray-card-${tray.barcode}`}
      >
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <div
                className={`tray-icon-container ${trayDetails?.trayCategory}`}
              >
                <img src={trayImg} alt="Tray Icon" className="tray-icon" />
              </div>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Typography variant="h6">{tray.barcode}</Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                >
                  <Chip
                    label={trayStatusDisplay}
                    className={`status-chip ${trayDetails?.status}`}
                  />
                  {isRejected && trayDetails?.statusDetails && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      sx={{ mt: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleViewRejectionDetails(
                          trayDetails?.statusDetails || {}
                        )
                      }}
                    >
                      View Rejection Details
                    </Button>
                  )}
                  {isRejected && isRep && (
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      sx={{ mt: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleFixRejectedTray(tray)
                      }}
                    >
                      Fix Rejected Tray
                    </Button>
                  )}
                  {isRepNotified && (
                    <Chip
                      label="Rep Notified ✅"
                      size="small"
                      className="rep-notified-chip"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              </Box>

              <Typography variant="body2" color="textSecondary">
                <strong>Vendor:</strong> {tray.companyName}
              </Typography>

              <Typography variant="body2" color="textSecondary">
                <strong>Created:</strong>{' '}
                {dayjs(tray.createdAt).format('MM/DD/YYYY h:mm A')}
              </Typography>

              <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleViewTrayDetails(tray)}
                  startIcon={<ReadMore />}
                >
                  Details
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAssignTray(tray)
                  }}
                  startIcon={<Person />}
                >
                  {trayDetails?.surgeryId ? 'Reassign' : 'Assign'}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEditTrayStatus(tray)
                  }}
                  startIcon={<Create />}
                >
                  Edit Status
                </Button>

                {(isSPD || isNurse) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSetTrayLocation(tray)
                    }}
                    startIcon={<Room />}
                  >
                    Set Location
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box className="surgery-trays-display">
      <Box
        className={classNames('trays-header', expanded && 'expanded')}
        onClick={onToggleExpanded}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onToggleExpanded && onToggleExpanded()
          }
        }}
      >
        <Typography variant="h6">Assigned Trays ({trays.length})</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={`${trays.length} ${trays.length === 1 ? 'Tray' : 'Trays'}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <IconButton size="small" tabIndex={-1}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box className="trays-content">{trays.map(renderTrayCard)}</Box>
      </Collapse>

      {/* Modals */}
      <EditTrayStatusModal
        open={isEditTrayStatusModalOpen}
        onClose={() => setIsEditTrayStatusModalOpen(false)}
        onConfirm={handleEditTrayStatusChange}
        trayCategory={selectedTrayCategory}
        isUpdatingTrayStatus={isUpdatingTrayStatus}
      />

      {selectedTray && (
        <EditTrayModal
          open={isListOfSurgeriesOpen && !!selectedTray}
          onClose={handleListOfSurgeriesClose}
          trayData={selectedTray?.trayItem as ItemResponse}
          onSuccess={handleSurgeryAssignmentSuccess}
        />
      )}

      <TrayRejectionDetailsModal
        isOpen={isRejectionDetailsModalOpen}
        onClose={handleRejectionDetailsModalClose}
        rejectionDetails={rejectionDetails}
      />

      <ChooseTrayLocationModal
        open={isShowChooseTrayLocationModalVisible}
        onClose={handleChooseTrayLocationModalClose}
        setTrays={(updater) => {
          // Call the original updater to update the trays
          const updatedTrays =
            typeof updater === 'function' ? updater(trays) : updater

          // Find the updated tray and call our success handler
          const updatedTray = updatedTrays.find(
            (tray: ItemResponse) => tray.id === selectedTray?.trayItem.id
          )
          if (updatedTray) {
            handleLocationUpdateSuccess(updatedTray)
          }
        }}
      />
    </Box>
  )
}

export default SurgeryTraysDisplay
